import React from 'react';
import P from 'prop-types';
import { object } from 'yup';
import { trim } from 'lodash';
import { graphql, compose } from 'react-apollo';
import ensureLoggedIn from 'utils/ensureLoggedIn';
import displayLoading from 'utils/displayLoading';
import { FORCE_PASSWORD_CHANGE_MUTATION } from 'gql/mutations';
import styled from 'styled-components';

import { Formik, Form } from 'formik';
import { alertError } from 'utils/alert';
import Button from 'components/Button/';
import ErrorMsg from 'components/form/ErrorMsg/';
import PasswordMaskField from 'components/form/PasswordMaskField/';
import { passwordSchema } from 'formSchemas/shared/password';

const schema = object().shape({
  password: passwordSchema,
});

// NOTE: This is for users who do NOT have a Cognito account
// who are attempting to change passwords.
// This will always happen after a failed login,
// where the GraphQL error is NEW_PASSWORD_REQUIRED.
const ChangePassword = props => {
  const { email, session, passwordChangeMutation } = props;

  return (
    <Wrapper>
      <Heading>Set your new password</Heading>
      <StyledDescription>Please update your password.</StyledDescription>
      <Formik
        initialValues={{ password: '' }}
        validationSchema={schema}
        onSubmit={async ({ password }) => {
          try {
            const { data } = await passwordChangeMutation({
              variables: { input: { email: trim(email).toLowerCase(), session, password } },
            });

            if (!data) throw new Error('no data');
            localStorage.setItem('accessToken', data.forcePasswordChange.session.id);
            window.location.reload();
          } catch (err) {
            alertError({ message: 'Update password failed. Please try again later.' });
          }
        }}
        render={({ isSubmitting, isValid, errors }) => (
          <Form>
            <StyledPasswordField hasError={!!errors.password}>
              <StyledLabel htmlFor="password">New password</StyledLabel>
              <PasswordMaskField id="password" name="password" />
              <ErrorMsg name="password" />
            </StyledPasswordField>
            <Button type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting ? 'Updating...' : 'Update password and login'}
            </Button>
          </Form>
        )}
      />
    </Wrapper>
  );
};

ChangePassword.propTypes = {
  passwordChangeMutation: P.func.isRequired,
  email: P.string.isRequired,
  session: P.string.isRequired,
  pushView: P.func,
};

export default compose(
  graphql(FORCE_PASSWORD_CHANGE_MUTATION, { name: 'passwordChangeMutation' }),
  ensureLoggedIn,
  displayLoading(),
)(ChangePassword);

const Wrapper = styled.div`
  width: 530px;
  margin-top: 140px;
  padding: 20px 30px;
  border-radius: 4px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
  background-color: #f7f7f7;
`;

const Heading = styled.h2`
  text-align: left;
  font-size: 24px;
  font-weight: bold;
`;

const StyledDescription = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
`;

const StyledPasswordField = styled.div(
  ({ theme: { colors }, hasError }) => `
  margin-bottom: 20px;
  input {
    width: 100%;
    border-radius: 4px;
    border: 1px solid rgb(209, 209, 209);
    padding: 11px 15px;
    outline: none;
    font-size: 14px;
    color: rgb(64, 64, 64);
    line-height: 1.25rem;
    height: 40px;
    transition: border-color 0.15s ease-in-out 0s, box-shadow 0.15s ease-in-out 0s;
    margin-bottom: 0.25rem !important;
    border: 1px solid ${hasError ? colors.roman : '#d1d1d1'};
    :focus {
      border: 1px solid #81bba8;
    }
  }`,
);

const StyledLabel = styled.label`
  font-weight: 900;
  margin-bottom: 5px;
  display: block;
`;
